import { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useExpenseCategories,
  useAddExpenseCategory,
  useUpdateExpenseCategory,
  useDeleteExpenseCategory,
} from '@/hooks/useExpenseCategories';
import type { ExpenseCategory } from '@jobos/shared';

const DEFAULT_COLOR = '#6b7280';
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function ExpenseCategoriesSettings() {
  const { data: categories = [], isLoading } = useExpenseCategories();
  const addMutation = useAddExpenseCategory();
  const updateMutation = useUpdateExpenseCategory();
  const deleteMutation = useDeleteExpenseCategory();

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(DEFAULT_COLOR);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed || !HEX_RE.test(newColor)) return;
    addMutation.mutate(
      { name: trimmed, color: newColor },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor(DEFAULT_COLOR);
        },
      }
    );
  };

  const handleStartEdit = (cat: ExpenseCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    const trimmed = editName.trim();
    if (!trimmed || !HEX_RE.test(editColor)) return;
    updateMutation.mutate(
      { id: editingId, name: trimmed, color: editColor },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, { onSuccess: () => setDeletingId(null) });
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Expense Categories</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Expense Categories</h2>
      <p className="text-sm text-gray-500 mb-6">
        Manage the categories available when adding job expenses. Color is used as a visual tag across expense views.
      </p>

      {/* Add new category */}
      <div className="flex items-center gap-2 mb-6">
        <Input
          placeholder="Category name (e.g. Materials)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="max-w-sm"
        />
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          className="h-9 w-12 rounded border border-gray-300 cursor-pointer bg-white p-1"
          aria-label="New category color"
        />
        <Button
          onClick={handleAdd}
          disabled={!newName.trim() || addMutation.isPending}
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Categories list */}
      <div className="border rounded-lg divide-y">
        {categories.map((cat) => (
          <div key={cat.id} className="px-4 py-2.5 hover:bg-gray-50 group flex items-center justify-between">
            {editingId === cat.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="max-w-sm h-8"
                  autoFocus
                />
                <input
                  type="color"
                  value={editColor}
                  onChange={(e) => setEditColor(e.target.value)}
                  className="h-8 w-10 rounded border border-gray-300 cursor-pointer bg-white p-1"
                  aria-label="Category color"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleSaveEdit}
                  disabled={!editName.trim() || updateMutation.isPending}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : deletingId === cat.id ? (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm text-red-600">Delete "{cat.name}"?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(cat.id)}
                  disabled={deleteMutation.isPending}
                >
                  Confirm
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span
                    className="inline-block h-4 w-4 rounded-full border border-gray-200"
                    style={{ backgroundColor: cat.color }}
                    aria-hidden
                  />
                  <span className="text-sm text-gray-900 font-medium">{cat.name}</span>
                  {cat.isDefault && (
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Lock className="h-3 w-3" />
                      Default
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleStartEdit(cat)}
                    className="h-7 w-7 p-0"
                  >
                    <Pencil className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeletingId(cat.id)}
                    className="h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
        {categories.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No categories yet. Add one above.
          </div>
        )}
      </div>
    </div>
  );
}
